import { InfoIcon } from '@chakra-ui/icons';
import { Flex, Tooltip } from '@chakra-ui/react';
import 'assets/scss/CohortTable.scss';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useMemo, useCallback, useRef } from 'react';

function CohortTable({
  cohortData,
  formatCell,
  aggRows,
  header,
}) {
  const tableRef = useRef(null);
  const lastHoveredCells = useRef([]);
  const hoverTimeoutRef = useRef(null);
  const currentHoverPos = useRef({ x: null, y: null });

  // Direct DOM manipulation for hover - bypasses React entirely!
  // Only applies hover if mouse STOPS on a cell (debounced)
  const handleMouseOver = useCallback((x, y) => {
    if (!tableRef.current) return;

    // Skip if already hovering this exact position
    if (currentHoverPos.current.x === x && currentHoverPos.current.y === y) {
      return;
    }

    // Cancel any pending hover update
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Wait for mouse to settle before highlighting (75ms debounce)
    hoverTimeoutRef.current = setTimeout(() => {
      // Double-check position hasn't changed (shouldn't happen, but safety check)
      const container = tableRef.current;
      if (!container) return;

      // Remove previous hover classes
      lastHoveredCells.current.forEach(cell => {
        cell.classList.remove('tw-cohort__cell--hovered');
      });
      lastHoveredCells.current = [];

      // Find and highlight matching cells using querySelector
      const matchingCells = [];

      if (x !== null) {
        const rowCells = container.querySelectorAll(`.tw-cohort__cell[data-row="${x}"]`);
        matchingCells.push(...rowCells);
      }
      if (y !== null) {
        const colCells = container.querySelectorAll(`.tw-cohort__cell[data-col="${y}"]`);
        matchingCells.push(...colCells);
      }

      // Add hover class to matching cells
      matchingCells.forEach(cell => {
        cell.classList.add('tw-cohort__cell--hovered');
      });

      lastHoveredCells.current = matchingCells;
      currentHoverPos.current = { x, y };
      hoverTimeoutRef.current = null;
    }, 75); // 75ms debounce - only highlight if mouse stops
  }, []);

  const handleMouseOut = useCallback(() => {
    // Cancel any pending hover update
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Remove hover classes from all previously hovered cells
    lastHoveredCells.current.forEach(cell => {
      cell.classList.remove('tw-cohort__cell--hovered');
    });
    lastHoveredCells.current = [];
    currentHoverPos.current = { x: null, y: null };
  }, []);

  const getWeightedValueMap = useCallback((isPredicted) => {
    const flattenedValues = cohortData.reduce((acc, row) => {
      return [
        ...acc,
        ...row.data
          .filter((item) => {
            return item.isPredicted === isPredicted;
          })
          .map((value) => value.value),
      ];
    }, []);

    flattenedValues.sort((a, b) => a - b);

    const weightedValueMap = flattenedValues.reduce((acc, value, index) => {
      return {
        ...acc,
        [value]: (index / flattenedValues.length) * 0.5,
      };
    }, {});

    return weightedValueMap;
  }, [cohortData]);

  const weightedValueMap = useMemo(() => {
    return getWeightedValueMap();
  }, [getWeightedValueMap]);

  const weightedValueMapPredicted = useMemo(() => {
    return getWeightedValueMap(true);
  }, [getWeightedValueMap]);

  const prefixColumnsNum = cohortData[0]?.prefixColumns?.length - 1 || 0;

  // Memoized data cell component - never re-renders on hover!
  const DataCell = React.memo(({ x, y, value, isPredicted, alphaValue }) => {
    const style = {
      background: isPredicted
        ? `rgba(100, 100, 100, ${alphaValue})`
        : `rgba(67, 24, 255, ${alphaValue})`,
    };

    return (
      <td
        data-text={formatCell ? formatCell(value) : value}
        data-row={x}
        data-col={y}
        className="tw-cohort__cell"
        style={style}
        onMouseOver={() => handleMouseOver(x, y)}
        onMouseOut={handleMouseOut}
      >
        {formatCell ? formatCell(value) : value}
      </td>
    );
  }, (prevProps, nextProps) => {
    // Only re-render if value changed - hover is handled by CSS!
    return (
      prevProps.value === nextProps.value &&
      prevProps.isPredicted === nextProps.isPredicted &&
      prevProps.alphaValue === nextProps.alphaValue
    );
  });

  const Header = () => {
    return (
      <thead>
        <tr className="tw-cohort__row tw-cohort__header">
          {/* index == prefixColumnsNum */}
          {header.map((item, index) => {
            return (
              <Fragment key={index}>
                {index === prefixColumnsNum + 1 && <th></th>}
                <th
                  key={`headerCell${item}`}
                  data-col={index}
                  className={classNames('tw-cohort__cell', {
                    'tw-cohort_cell__prefix_cell': index <= prefixColumnsNum,
                  })}
                >
                  {item}
                </th>
              </Fragment>
            );
          })}
          {/* seperator */}
        </tr>
      </thead>
    );
  };

  const AggregatedRow = ({ row }) => {
    if (!row) {
      return null;
    }

    const { data, label, tooltip } = row;

    return (
      <>
        <tr className="tw-cohort__row tw-cohort__agg-row">
          <td colSpan={prefixColumnsNum + 1} className="tw-cohort__cell">
            <Flex align="center" gap={1}>
              <span>{label}</span>
              {tooltip && (
                <Tooltip label={tooltip} placement="right" hasArrow>
                  <InfoIcon
                    boxSize={3}
                    color="gray.500"
                    cursor="help"
                    aria-label={`${label} calculation details`}
                  />
                </Tooltip>
              )}
            </Flex>
          </td>
          <td className={'tw-cohort_cell__separator'}></td>
          {data.map((value, y) => {
            return (
              <td
                key={`aggRowCell${y}`}
                data-col={y}
                onMouseOver={() => handleMouseOver(null, y)}
                onMouseOut={handleMouseOut}
                className="tw-cohort__cell"
              >
                <div> {formatCell ? formatCell(value) : value}</div>
              </td>
            );
          })}
        </tr>
      </>
    );
  };

  const rowLength = cohortData[0]?.data?.length || 0;

  return (
    <div className="tw-cohort" ref={tableRef}>
      <table>
        <Header />
        <tbody>
          {cohortData.map((row, x) => {
            let isFirstPredicted = true;
            return (
              <tr key={`row${x}`} className={classNames('tw-cohort__row')}>
                {row.prefixColumns.map((value, y) => {
                  return (
                    <td
                      data-text={value}
                      data-row={x}
                      key={`extraCell${x}${y}`}
                      className="tw-cohort_cell__prefix_cell tw-cohort__cell"
                      onMouseOver={() => handleMouseOver(x, null)}
                      onMouseOut={handleMouseOut}
                    >
                      {value}
                    </td>
                  );
                })}
                <td className={'tw-cohort_cell__separator'}></td>
                {row.data &&
                  (() => {
                    const cells = [];
                    for (let y = 0; y < rowLength; y++) {

                      const cell = row.data[y];

                      if (!cell) {
                        cells.push(<td key={`cell${x}${y}`}></td>);
                        continue;
                      }

                      const { value, isPredicted } = cell;

                      const alphaValue = isPredicted
                        ? weightedValueMapPredicted[value] + 0.1
                        : weightedValueMap[value] + 0.1;

                      if (isPredicted && isFirstPredicted) {
                        isFirstPredicted = false;
                      }

                      cells.push(
                        <DataCell
                          key={`cell${x}${y}`}
                          x={x}
                          y={y}
                          value={value}
                          isPredicted={isPredicted}
                          alphaValue={alphaValue}
                        />
                      );
                    }
                    return cells;
                  })()}
                {row.postfixColumns?.map((value, y) => {
                  return (
                    <td
                      data-text={value}
                      data-row={x}
                      key={`extraCell${x}${y}`}
                      className="tw-cohort_cell__postfix_cell tw-cohort__cell"
                      onMouseOver={() => handleMouseOver(x, null)}
                      onMouseOut={handleMouseOut}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {
            // render aggregated rows
            <>
              <tr className="tw-cohort__row tw-cohort__row-seperator"></tr>
              {aggRows &&
                aggRows.map((row, index) => {
                  return <AggregatedRow key={`aggRow${index}`} row={row} />;
                })}
            </>
          }
        </tbody>
      </table>
    </div>
  );
}

CohortTable.propTypes = {
  cohortData: PropTypes.arrayOf(
    PropTypes.shape({
      prefixColumns: PropTypes.arrayOf(PropTypes.any),
      data: PropTypes.arrayOf(PropTypes.any),
    })
  ).isRequired,
  aggRows: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.any),
      label: PropTypes.string,
      tooltip: PropTypes.string,
    })
  ),
  formatCell: PropTypes.func,
  header: PropTypes.arrayOf(PropTypes.any),
};

export default CohortTable;
